/**
 * Entrepreneurship Module Component
 * "Start Your Business" tab for Academy courses with BUILD integration
 */

import { useState } from 'react';
import { 
  GraduationCap, Calculator, FileCheck, Shield, TrendingUp,
  DollarSign, Target, ChevronRight, Check, Crown, Star,
  Building, Truck, Users, Briefcase, ArrowRight, Sparkles,
  Mail, CheckCircle, Circle, Lock
} from 'lucide-react';
import { BUILD_PACKAGES, ENTREPRENEURSHIP_ROADMAP, ENTREPRENEURSHIP_MODULES } from '../../entrepreneurshipData';
import { RevenueCalculator, BreakEvenCalculator, RateNegotiationSimulator } from './InteractiveCalculators';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  GraduationCap, Calculator, FileCheck, Shield, TrendingUp,
  DollarSign, Target, Building, Truck, Users, Briefcase
};

// Startup Cost Calculator Component
const StartupCostCalculator = ({ courseId, startupCosts }) => {
  const [budget, setBudget] = useState('mid'); // low, mid, high
  
  if (!startupCosts) return null;
  
  const totals = {
    low: startupCosts.low,
    mid: startupCosts.mid,
    high: startupCosts.high
  };
  
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6" data-testid="startup-calculator">
      <div className="flex items-center gap-3 mb-4">
        <Calculator className="w-6 h-6 text-[#D4AF37]" />
        <h3 className="text-white font-bold text-lg">Startup Cost Calculator</h3>
      </div>
      
      {/* Budget Selector */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'low', label: 'Bootstrap', desc: 'Minimum viable' },
          { key: 'mid', label: 'Standard', desc: 'Recommended' },
          { key: 'high', label: 'Premium', desc: 'Full setup' }
        ].map((option) => (
          <button
            key={option.key}
            onClick={() => setBudget(option.key)}
            className={`flex-1 p-3 border transition-all ${
              budget === option.key 
                ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-white' 
                : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <div className="font-bold text-sm">{option.label}</div>
            <div className="text-xs opacity-60">{option.desc}</div>
          </button>
        ))}
      </div>
      
      {/* Cost Breakdown */}
      <div className="space-y-3 mb-6">
        {startupCosts.breakdown.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5">
            <div>
              <span className="text-white text-sm">{item.item}</span>
              {item.note && (
                <span className="ml-2 text-[#D4AF37] text-xs">({item.note})</span>
              )}
            </div>
            <span className="text-white font-mono">${item[budget].toLocaleString()}</span>
          </div>
        ))}
      </div>
      
      {/* Total */}
      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 flex items-center justify-between">
        <span className="text-white font-bold">Estimated Total</span>
        <span className="text-[#D4AF37] text-2xl font-bold">${totals[budget].toLocaleString()}</span>
      </div>
      
      <p className="text-white/40 text-xs mt-3">
        * Estimates based on industry averages. Actual costs may vary by location and circumstances.
      </p>
    </div>
  );
};

// Career Path Card Component
const CareerPathCard = ({ path, onSelectBuild }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div 
      className={`border transition-all ${
        path.recommended 
          ? 'bg-[#D4AF37]/5 border-[#D4AF37]/30' 
          : 'bg-white/5 border-white/10'
      }`}
    >
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-white font-bold">{path.title}</h4>
              {path.recommended && (
                <span className="px-2 py-0.5 bg-[#D4AF37] text-black text-xs font-bold uppercase">
                  Recommended
                </span>
              )}
            </div>
            <p className="text-white/60 text-sm">{path.description}</p>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-white/40">Timeline: <span className="text-white">{path.timeline}</span></span>
              <span className="text-white/40">Potential: <span className="text-green-400">{path.earnings}</span></span>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-white/40 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/10 pt-4">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <h5 className="text-green-400 text-sm font-bold mb-2">Pros</h5>
              <ul className="space-y-1">
                {path.pros.map((pro, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="text-red-400 text-sm font-bold mb-2">Cons</h5>
              <ul className="space-y-1">
                {path.cons.map((con, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                    <span className="w-3 h-3 text-red-400 flex-shrink-0">•</span>
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {path.buildIntegration && (
            <button
              onClick={onSelectBuild}
              className="w-full mt-2 px-4 py-3 bg-[#D4AF37] text-black font-bold flex items-center justify-center gap-2 hover:bg-[#C4A030] transition-colors"
            >
              <Crown className="w-5 h-5" />
              Start This Path with Queen B BUILD
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// BUILD Package Card Component
const BuildPackageCard = ({ pkg, packageKey, isPopular }) => {
  return (
    <div 
      className={`border p-6 flex flex-col ${
        isPopular 
          ? 'bg-[#D4AF37]/10 border-[#D4AF37]' 
          : 'bg-white/5 border-white/10'
      }`}
    >
      {isPopular && (
        <div className="flex items-center gap-1 text-[#D4AF37] text-xs font-bold uppercase mb-2">
          <Star className="w-4 h-4" />
          Most Popular
        </div>
      )}
      
      <h3 className="text-white text-xl font-bold mb-1">{pkg.name}</h3>
      <p className="text-white/60 text-sm mb-4">{pkg.tagline}</p>
      
      <div className="mb-4">
        <span className="text-3xl font-bold" style={{ color: pkg.color }}>${pkg.price.toLocaleString()}</span>
        <span className="text-white/40 text-sm ml-2">one-time</span>
      </div>
      
      <div className="text-white/50 text-sm mb-4">
        {pkg.supportDays}-Day Launch Support
      </div>
      
      <ul className="space-y-2 mb-6 flex-1">
        {pkg.features.slice(0, 6).map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
            <span className="text-white/80">{feature}</span>
          </li>
        ))}
        {pkg.features.length > 6 && (
          <li className="text-white/40 text-sm">+{pkg.features.length - 6} more</li>
        )}
      </ul>
      
      <p className="text-white/50 text-xs mb-4">{pkg.bestFor}</p>
      
      <a
        href="/starter-pack"
        className={`w-full py-3 font-bold text-center transition-colors ${
          isPopular 
            ? 'bg-[#D4AF37] text-black hover:bg-[#C4A030]' 
            : 'bg-white/10 text-white hover:bg-white/20'
        }`}
      >
        Select {pkg.name}
      </a>
    </div>
  );
};

// Roadmap Step Component
const RoadmapStep = ({ step, isComplete, isCurrent }) => {
  const Icon = ICONS[step.icon] || Circle;
  
  return (
    <div className={`flex items-start gap-4 ${isCurrent ? 'opacity-100' : isComplete ? 'opacity-100' : 'opacity-50'}`}>
      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
        isComplete ? 'bg-green-500/20' : isCurrent ? 'bg-[#D4AF37]/20' : 'bg-white/5'
      }`}>
        {isComplete ? (
          <CheckCircle className="w-5 h-5 text-green-400" />
        ) : isCurrent ? (
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        ) : (
          <Lock className="w-4 h-4 text-white/30" />
        )}
      </div>
      <div className="flex-1 pb-6 border-l-2 border-white/10 pl-4 -ml-5">
        <h4 className={`font-bold ${isComplete ? 'text-green-400' : isCurrent ? 'text-[#D4AF37]' : 'text-white/50'}`}>
          Step {step.step}: {step.title}
        </h4>
        <p className="text-white/60 text-sm">{step.description}</p>
      </div>
    </div>
  );
};

// Main Entrepreneurship Module Component
const EntrepreneurshipModule = ({ courseId, courseProgress = 0, onClose }) => {
  const [activeView, setActiveView] = useState('overview'); // overview, paths, calculator, build
  
  const moduleData = ENTREPRENEURSHIP_MODULES[courseId];
  
  if (!moduleData) {
    return (
      <div className="p-8 text-center text-white/50">
        <Building className="w-12 h-12 mx-auto mb-4 text-white/20" />
        <p>Entrepreneurship module not available for this course yet.</p>
      </div>
    );
  }
  
  // Determine roadmap progress based on course progress
  const getStepStatus = (stepNum) => {
    if (stepNum === 1) return courseProgress >= 100 ? 'complete' : 'current';
    if (stepNum === 2 && courseProgress >= 100) return 'current';
    return 'upcoming';
  };
  
  return (
    <div className="flex-1 overflow-y-auto" data-testid="entrepreneurship-module">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-transparent p-6 border-b border-white/10">
        <div className="flex items-center gap-2 text-[#D4AF37] text-sm font-bold uppercase mb-2">
          <Sparkles className="w-4 h-4" />
          Start Your Business
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{moduleData.title}</h2>
        <p className="text-white/60">{moduleData.description}</p>
      </div>
      
      {/* Navigation Tabs */}
      <div className="flex border-b border-white/10 overflow-x-auto">
        {[
          { id: 'overview', label: 'Roadmap', icon: Target },
          { id: 'paths', label: 'Career Paths', icon: Briefcase },
          { id: 'calculator', label: 'Calculators', icon: Calculator },
          { id: 'build', label: 'BUILD Packages', icon: Crown }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeView === tab.id 
                  ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      
      {/* Content */}
      <div className="p-4 md:p-6">
        {/* Overview / Roadmap */}
        {activeView === 'overview' && (
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <h3 className="text-white font-bold text-lg mb-6">Your Path to Business Ownership</h3>
              <div className="space-y-2">
                {ENTREPRENEURSHIP_ROADMAP.map((step) => (
                  <RoadmapStep 
                    key={step.step}
                    step={step}
                    isComplete={getStepStatus(step.step) === 'complete'}
                    isCurrent={getStepStatus(step.step) === 'current'}
                  />
                ))}
              </div>
            </div>
            
            {/* CTA */}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6">
              <h3 className="text-white font-bold text-lg mb-2">{moduleData.callToAction.title}</h3>
              <p className="text-white/60 mb-4">{moduleData.callToAction.description}</p>
              <button
                onClick={() => setActiveView('build')}
                className="px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] transition-colors flex items-center gap-2"
              >
                {moduleData.callToAction.buttonText}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Career Paths */}
        {activeView === 'paths' && (
          <div className="space-y-4">
            <p className="text-white/60 mb-4">{moduleData.subtitle}</p>
            {moduleData.careerPaths.map((path, idx) => (
              <CareerPathCard 
                key={idx}
                path={path}
                onSelectBuild={() => setActiveView('build')}
              />
            ))}
          </div>
        )}
        
        {/* Calculators */}
        {activeView === 'calculator' && (
          <div className="space-y-6">
            <StartupCostCalculator 
              courseId={courseId}
              startupCosts={moduleData.startupCosts}
            />
            
            {/* Revenue Calculator */}
            <RevenueCalculator courseType={courseId} />
            
            {/* Break-Even Calculator */}
            <BreakEvenCalculator courseType={courseId} />
            
            {/* Rate Negotiation Simulator */}
            <RateNegotiationSimulator />
          </div>
        )}
        
        {/* BUILD Packages */}
        {activeView === 'build' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Launch Your Business with Queen B BUILD</h3>
              <p className="text-white/60">We handle the paperwork and compliance. You focus on building your empire.</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <BuildPackageCard pkg={BUILD_PACKAGES.foundation} packageKey="foundation" />
              <BuildPackageCard pkg={BUILD_PACKAGES.accelerator} packageKey="accelerator" isPopular />
              <BuildPackageCard pkg={BUILD_PACKAGES.elite} packageKey="elite" />
            </div>
            
            {/* Contact CTA */}
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <h4 className="text-white font-bold mb-2">Not sure which package is right for you?</h4>
              <p className="text-white/60 text-sm mb-4">Schedule a free consultation with our team.</p>
              <div className="flex justify-center">
                <a 
                  href="mailto:dispatch@queenblogistics.com"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email Us
                </a>
              </div>
            </div>
            
            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4">
                <div className="text-2xl font-bold text-[#D4AF37]">500+</div>
                <div className="text-white/50 text-sm">Authorities Launched</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-[#D4AF37]">30 Days</div>
                <div className="text-white/50 text-sm">Average Setup Time</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-[#D4AF37]">98%</div>
                <div className="text-white/50 text-sm">Success Rate</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntrepreneurshipModule;
